// src/pages/Orders/components/modals/KarigarQueueModal.jsx
// ============================================================================
// ENHANCED KARIGAR QUEUE MODAL
// Shows karigar's job queue with detailed info from both systems
// ============================================================================

import React, { useState, useMemo } from "react";
import {
  XCircleIcon,
  BriefcaseIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  UserIcon,
  ChartBarIcon,
  CalendarIcon,
  LightningBoltIcon,
  EyeIcon,
} from "@heroicons/react/outline";
import Badge from "../../../../components/common/Badge";
import Button from "../../../../components/common/Button";

// Status configuration
const STATUS_CONFIG = {
  PENDING: { color: "gray", label: "Pending", priority: 1 },
  ASSIGNED: { color: "yellow", label: "Assigned", priority: 2 },
  ACCEPTED: { color: "blue", label: "Accepted", priority: 3 },
  IN_PROGRESS: { color: "indigo", label: "In Progress", priority: 4 },
  ON_HOLD: { color: "orange", label: "On Hold", priority: 3 },
  QUALITY_CHECK: { color: "purple", label: "Quality Check", priority: 5 },
  REVISION_REQUIRED: { color: "amber", label: "Revision", priority: 4 },
  COMPLETED: { color: "green", label: "Completed", priority: 6 },
  DELIVERED: { color: "emerald", label: "Delivered", priority: 7 },
  CANCELLED: { color: "red", label: "Cancelled", priority: 0 },
};

const PRIORITY_CONFIG = {
  CRITICAL: { color: "red", weight: 5 },
  URGENT: { color: "orange", weight: 4 },
  HIGH: { color: "amber", weight: 3 },
  NORMAL: { color: "gray", weight: 2 },
  LOW: { color: "slate", weight: 1 },
};

const KarigarQueueModal = ({
  karigarId,
  queue,
  loading,
  onClose,
  formatDate,
  onViewJobDetails, // Optional: callback to view full job details
}) => {
  const [activeTab, setActiveTab] = useState("active"); // active | all | completed
  const [sortBy, setSortBy] = useState("priority"); // priority | dueDate | status

  // Process queue data
  const karigarInfo = queue?.karigar || {};
  const capacity = queue?.capacity || {};
  const allJobs = useMemo(() => {
    // Flatten queue object into array if needed
    if (queue?.queue) {
      const jobs = [];
      Object.entries(queue.queue).forEach(([status, statusJobs]) => {
        if (Array.isArray(statusJobs)) {
          statusJobs.forEach((job) =>
            jobs.push({ ...job, status: status.toUpperCase() })
          );
        }
      });
      return jobs;
    }
    return queue?.jobs || [];
  }, [queue]);

  // Filter jobs based on tab
  const filteredJobs = useMemo(() => {
    let jobs = [...allJobs];

    if (activeTab === "active") {
      jobs = jobs.filter(
        (j) => !["COMPLETED", "DELIVERED", "CANCELLED"].includes(j.status)
      );
    } else if (activeTab === "completed") {
      jobs = jobs.filter((j) => ["COMPLETED", "DELIVERED"].includes(j.status));
    }

    // Sort
    if (sortBy === "priority") {
      jobs.sort((a, b) => {
        const aPriority = PRIORITY_CONFIG[a.priority]?.weight || 2;
        const bPriority = PRIORITY_CONFIG[b.priority]?.weight || 2;
        return bPriority - aPriority;
      });
    } else if (sortBy === "dueDate") {
      jobs.sort((a, b) => {
        const aDate = a.expectedCompletionDate
          ? new Date(a.expectedCompletionDate)
          : new Date("2099-12-31");
        const bDate = b.expectedCompletionDate
          ? new Date(b.expectedCompletionDate)
          : new Date("2099-12-31");
        return aDate - bDate;
      });
    } else if (sortBy === "status") {
      jobs.sort((a, b) => {
        const aOrder = STATUS_CONFIG[a.status]?.priority || 0;
        const bOrder = STATUS_CONFIG[b.status]?.priority || 0;
        return bOrder - aOrder;
      });
    }

    return jobs;
  }, [allJobs, activeTab, sortBy]);

  // Calculate stats
  const stats = useMemo(() => {
    const activeJobs = allJobs.filter(
      (j) => !["COMPLETED", "DELIVERED", "CANCELLED"].includes(j.status)
    );
    const urgentJobs = allJobs.filter((j) =>
      ["URGENT", "CRITICAL"].includes(j.priority)
    );
    const overdueJobs = allJobs.filter((j) => {
      if (!j.expectedCompletionDate) return false;
      if (["COMPLETED", "DELIVERED", "CANCELLED"].includes(j.status))
        return false;
      return new Date() > new Date(j.expectedCompletionDate);
    });

    return {
      total: allJobs.length,
      active: activeJobs.length,
      urgent: urgentJobs.length,
      overdue: overdueJobs.length,
      completed: allJobs.filter((j) =>
        ["COMPLETED", "DELIVERED"].includes(j.status)
      ).length,
    };
  }, [allJobs]);

  // Check if job is overdue
  const isOverdue = (job) => {
    if (!job.expectedCompletionDate) return false;
    if (["COMPLETED", "DELIVERED", "CANCELLED"].includes(job.status))
      return false;
    return new Date() > new Date(job.expectedCompletionDate);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <UserIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {karigarInfo.name || "Karigar"}'s Queue
                </h3>
                <p className="text-white/80 text-sm">
                  {karigarInfo.karigarCode || karigarId}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            >
              <XCircleIcon className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Capacity Bar */}
          {capacity.maxJobs && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-white/80 text-xs mb-1">
                <span>Capacity Utilization</span>
                <span>
                  {capacity.activeJobs || 0} / {capacity.maxJobs} jobs (
                  {capacity.utilizationPercent || 0}%)
                </span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    (capacity.utilizationPercent || 0) >= 90
                      ? "bg-red-400"
                      : (capacity.utilizationPercent || 0) >= 70
                      ? "bg-amber-400"
                      : "bg-green-400"
                  }`}
                  style={{ width: `${capacity.utilizationPercent || 0}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 grid grid-cols-5 gap-3">
          <div className="text-center">
            <p className="text-lg font-bold text-gray-900">{stats.total}</p>
            <p className="text-xs text-gray-500">Total</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-blue-600">{stats.active}</p>
            <p className="text-xs text-gray-500">Active</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-orange-600">{stats.urgent}</p>
            <p className="text-xs text-gray-500">Urgent</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-red-600">{stats.overdue}</p>
            <p className="text-xs text-gray-500">Overdue</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold text-green-600">
              {stats.completed}
            </p>
            <p className="text-xs text-gray-500">Done</p>
          </div>
        </div>

        {/* Tabs & Sort */}
        <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab("active")}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activeTab === "active"
                  ? "bg-white text-primary shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Active ({stats.active})
            </button>
            <button
              onClick={() => setActiveTab("all")}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activeTab === "all"
                  ? "bg-white text-primary shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              All ({stats.total})
            </button>
            <button
              onClick={() => setActiveTab("completed")}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activeTab === "completed"
                  ? "bg-white text-primary shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Completed ({stats.completed})
            </button>
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5"
          >
            <option value="priority">Sort by Priority</option>
            <option value="dueDate">Sort by Due Date</option>
            <option value="status">Sort by Status</option>
          </select>
        </div>

        {/* Job List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredJobs.length > 0 ? (
            <div className="space-y-3">
              {filteredJobs.map((job, index) => {
                const statusConfig = STATUS_CONFIG[job.status] || {
                  color: "gray",
                  label: job.status,
                };
                const priorityConfig = PRIORITY_CONFIG[job.priority] || {
                  color: "gray",
                };
                const overdue = isOverdue(job);

                return (
                  <div
                    key={job._id || job.jobNumber || index}
                    className={`p-4 rounded-lg border-2 transition-all hover:shadow-md ${
                      overdue
                        ? "bg-red-50 border-red-200"
                        : "bg-white border-gray-200"
                    }`}
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">
                            {job.jobNumber || `Job #${index + 1}`}
                          </span>
                          {job.priority && job.priority !== "NORMAL" && (
                            <Badge variant={priorityConfig.color} size="sm">
                              {job.priority}
                            </Badge>
                          )}
                          {overdue && (
                            <Badge variant="red" size="sm">
                              <ExclamationCircleIcon className="w-3 h-3 mr-1 inline" />
                              Overdue
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-0.5">
                          {job.productName || job.title || "Unknown Product"}
                        </p>
                      </div>
                      <Badge variant={statusConfig.color}>
                        {statusConfig.label}
                      </Badge>
                    </div>

                    {/* Details Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mt-3">
                      <div>
                        <p className="text-gray-500 mb-0.5">Order</p>
                        <p className="font-medium text-gray-900">
                          {job.orderId || job.orderNumber || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-0.5">Work Type</p>
                        <p className="font-medium text-gray-900">
                          {job.workType || job.jobType || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-0.5">Assigned</p>
                        <p className="font-medium text-gray-900">
                          {job.assignedAt || job.timeline?.assignedAt
                            ? formatDate(
                                job.assignedAt || job.timeline?.assignedAt
                              )
                            : "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-0.5">Due</p>
                        <p
                          className={`font-medium ${
                            overdue ? "text-red-600" : "text-gray-900"
                          }`}
                        >
                          {job.expectedCompletionDate
                            ? formatDate(job.expectedCompletionDate)
                            : "-"}
                        </p>
                      </div>
                    </div>

                    {/* SKU & Specifications */}
                    {(job.productSKU || job.specifications) && (
                      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-500">
                        {job.productSKU && <span>SKU: {job.productSKU}</span>}
                        {job.specifications?.size && (
                          <span>Size: {job.specifications.size}</span>
                        )}
                        {job.specifications?.metalType && (
                          <span>Metal: {job.specifications.metalType}</span>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    {onViewJobDetails && (
                      <div className="mt-3 pt-3 border-t border-gray-100 flex justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onViewJobDetails(job)}
                          className="text-xs"
                        >
                          <EyeIcon className="w-3 h-3 mr-1" />
                          View Details
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <BriefcaseIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">
                {activeTab === "active"
                  ? "No active jobs"
                  : activeTab === "completed"
                  ? "No completed jobs"
                  : "No jobs in queue"}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {queue?.totalJobs !== undefined && (
              <span>Total: {queue.totalJobs} jobs</span>
            )}
            {queue?.overdueJobs > 0 && (
              <span className="ml-3 text-red-600">
                <ExclamationCircleIcon className="w-3 h-3 inline mr-1" />
                {queue.overdueJobs} overdue
              </span>
            )}
          </div>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default KarigarQueueModal;
